import * as React from 'react';
import {
  BodyText,
  Button,
  Grid,
  Header3Text,
  ModalWindow,
  NumberInput,
  Select,
  SpruceField,
  SpruceHelpInlineLink,
  SwitchDialogWindows,
  TextInput,
} from 'ui-toolkit';
import { FieldProps, Form, Formik } from 'formik';
import { EscrowWireDisbursementCategoriesOptions } from './EscrowWireDisbursementCategoriesOptions';
import { ICopyACHPushDetails } from './interfaces/ICopyACHPushDetails';
import { createWireFromACHSchema } from './createWireFromACHSchema';
import { convertACHPushToFormValues } from './convertACHPushToFormValues';
import { IFormValues } from './interfaces/IFormValues';
import { TextInputLimitValue } from 'components/order/escrow/escrowCreateEdit/confirmEscrowModal/TextInputLimitValue';
import { useGetBankNameByRoutingNumberQuery } from '../../../../../../API/order/escrow/EscrowBaseAPI';
import { useState } from 'react';

export interface ICreateWireFromACHModalProps {
  achPush: ICopyACHPushDetails;
  errorMessages: string[];
  isSaved: boolean;
  onAcknowledgeSuccess: () => void;
  onClose: () => void;
  onDeleteACHPush: () => void;
  onValidSubmit: (values: IFormValues) => void;
}

export const CreateWireFromACHModal: React.FC<ICreateWireFromACHModalProps> = ({
  achPush,
  errorMessages,
  isSaved,
  onAcknowledgeSuccess,
  onClose,
  onDeleteACHPush,
  onValidSubmit,
}) => {
  let [routingNumberLookup, setRoutingNumberLookup] = useState<string | null>('');

  const { data: bank } = useGetBankNameByRoutingNumberQuery(routingNumberLookup ?? '', {
    skip: routingNumberLookup?.length !== 9,
  });

  return (
    <ModalWindow title="Create Wire">
      <Formik
        initialValues={convertACHPushToFormValues(achPush)}
        validationSchema={createWireFromACHSchema}
        onSubmit={() => {}}
        validateOnMount={true}
      >
        {({ isSubmitting, values, isValid }) => (
          <Form>
            <SwitchDialogWindows
              errorMessage={
                <>
                  {errorMessages.map((m, i) => (
                    <BodyText key={i}>{m}</BodyText>
                  ))}
                </>
              }
              errorTitle={
                <Header3Text>
                  Wire was not added. Please <SpruceHelpInlineLink /> to submit a request for help.
                </Header3Text>
              }
              isCompleted={isSaved}
              isError={errorMessages.length > 0}
              isLoading={isSubmitting}
              loadingTitle="Working..."
              onCloseClick={() => {
                if (isSaved && errorMessages.length === 0) onAcknowledgeSuccess();
                onClose();
              }}
              successTitle="The wire was created"
              successMessage={
                <>
                  <BodyText>Would you like to delete the ACH Push?</BodyText>
                  <BodyText>
                    <Button type="button" intent={'ghost'} onClick={onDeleteACHPush}>
                      Yes, delete the ACH Push!
                    </Button>
                  </BodyText>
                </>
              }
            >
              <ModalWindow uitype="fit" title="Create Wire" onClose={onClose}>
                <Grid columnCount={2}>
                  <SpruceField name="beneficiary_name">
                    {({ field, form }: FieldProps) => (
                      <div style={{ gridColumn: 'span 2' }}>
                        <TextInput {...field} label="Beneficiary Name" readOnly={true} />
                      </div>
                    )}
                  </SpruceField>
                  <SpruceField name="routing_number">
                    {({ field }: FieldProps) => (
                      <TextInput
                        {...field}
                        onChange={(value) => {
                          field.onChange(value);
                          setRoutingNumberLookup(value);
                        }}
                        autoFocus={true}
                        label="Routing Number"
                        hintText={bank?.name}
                      />
                    )}
                  </SpruceField>
                  <SpruceField name="account_number">
                    {({ field }: FieldProps) => <TextInput {...field} label="Account Number" readOnly={true} />}
                  </SpruceField>
                  <SpruceField name="memo">
                    {({ field }: FieldProps) => (
                      <div style={{ gridColumn: 'span 2' }}>
                        <TextInputLimitValue {...field} label="Memo/OBI" />
                      </div>
                    )}
                  </SpruceField>
                  <SpruceField name="amount">
                    {({ field }: FieldProps) => <NumberInput {...field} label="Amount" mode="currency" />}
                  </SpruceField>
                  <SpruceField name="category" inputType="select">
                    {({ field }: FieldProps) => (
                      <Select
                        {...field}
                        label="Category"
                        options={EscrowWireDisbursementCategoriesOptions}
                        value={
                          field.value
                            ? EscrowWireDisbursementCategoriesOptions.find((t) => t.value === field.value)
                            : undefined
                        }
                      />
                    )}
                  </SpruceField>
                  <div style={{ display: 'flex' }}>
                    <Button intent={'primary'} disabled={!isValid} type="button" onClick={() => onValidSubmit(values)}>
                      Create Wire
                    </Button>
                    <Button intent={'ghost'} onClick={onClose}>
                      Cancel
                    </Button>
                  </div>
                </Grid>
              </ModalWindow>
            </SwitchDialogWindows>
          </Form>
        )}
      </Formik>
    </ModalWindow>
  );
};
